// Dashboard метрики — один эндпоинт GET /reports/dashboard под правом
// `reports:dashboard`.
//
// Раньше экран собирал те же цифры из /reports/sales, /subscriptions и
// /b24/deals. Все три открыты администратору (`reports:branch_finance`), а
// `reports:dashboard` прятало только пункт меню — админ, набравший /dashboard
// руками, финансовую сводку школы видел. См. docs/service/10-reports.md §5.

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';

import { getDashboardSummary, type DashboardSummary } from '@/features/reports/api/reports.api';
import { useTenantId } from '@/lib/tenant-context';

/** Локальная YYYY-MM-DD без сдвига TZ (toISOString даёт UTC). */
function toLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Диапазон текущего календарного месяца: первый → последний день. */
export function currentMonthRange(now: Date = new Date()): { from: string; to: string } {
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { from: toLocalDate(first), to: toLocalDate(last) };
}

/** Метрики дашборда за текущий месяц: выручка, абонементы, сделки B24. */
export function useDashboardSummary(): UseQueryResult<DashboardSummary, Error> {
  const tenantId = useTenantId();
  const range = useMemo(() => currentMonthRange(), []);
  return useQuery({
    queryKey: ['reports', tenantId, 'dashboard', range] as const,
    queryFn: () => getDashboardSummary(range),
    staleTime: 60_000,
    // 403 не ретраим: у роли просто нет права на дашборд.
    retry: false,
  });
}
